// 454 (T-3): the single DB-fed source for the FE's category constants — replaces the hardcoded
// ITEM_CATEGORIES / ITEM_CATEGORY_LABELS and the SIX duplicated CAT_TO_STORAGE maps. Fetches
// GET /api/category-constants once, caches, and HYDRATES the exported containers IN PLACE (same
// references the importers hold) from the DB rows (ordered by sort_order).
//
// Byte-identical: the literals below are the current values (fallback); the DB is backfilled to the
// SAME values (T-2), so hydration is a no-op today and a future 6th constant flows in automatically.
// This is NOT the 452 fail-open bug: the fallback is the CORRECT current data, not a permissive
// wildcard; the loaded-gated hook/getters below expose an explicit isLoaded for surfaces that want it.
import { useEffect, useState } from 'react';
import api from '../services/api';

// Mutable containers (importers hold these references; hydration mutates their CONTENTS in place).
export const ITEM_CATEGORIES = ['eggs', 'feed', 'medicine', 'birds', 'droppings'];
export const ITEM_CATEGORY_LABELS = { eggs: 'Eggs', feed: 'Feed', medicine: 'Medicine', birds: 'Birds', droppings: 'Droppings' };
// (CAT_TO_STORAGE removed by the storage-type collapse — storages are keyed by constant_key directly;
// item.category IS the constant, so pickers filter storage.constant_key == item.category, no map.)
// 457 (T-5 fix): constant -> attached_to_band (band is the prod/consumption ALLOCATION tag; §11/455).
// The 5 are all band-attached (fallback); a created non-flock constant is false -> produces/consumes
// band-LESS. 472 (resolver align): catAttachedToBand() resolves band-required ONLY for a constant that
// is EXPLICITLY attached_to_band=true — an unknown/not-yet-loaded key returns FALSE, matching the
// server's constantAttachedToBand default (server.js constantAttachedToBand => `!!map[cat]`). The 5
// built-ins live in the fallback literal, so they still resolve TRUE even before the DB hydrates; only
// a band-less category (Equipment, attached_to_band=false) or a typo'd/unhydrated key yields FALSE.
export const CAT_ATTACHED_TO_BAND = { eggs: true, feed: true, medicine: true, birds: true, droppings: true };
export const catAttachedToBand = (cat) => CAT_ATTACHED_TO_BAND[String(cat || '').toLowerCase()] === true;

let _loaded = false;
let _inflight = null;

// 466 W8 — the ONE item-category label reader. Returns the item's live item_category_labels.name,
// else the live constant display_label (French), NEVER the raw category key. Replaces the duplicated
// itemCatLabel copies in GitTab / ReceiptPanel / LineSafeEditPanel and the raw-key fallbacks elsewhere.
export function itemCategoryLabel(item) {
  const name = item?.item_category_labels?.name;
  if (name) return name;
  const cat = String(item?.category || '').toLowerCase();
  return ITEM_CATEGORY_LABELS[cat] || 'Other';
}

// 466 W9 — the storage→item prefilter: selecting a STORAGE narrows the item list to items whose category
// matches that storage's constant_key (the shared predicate storage.constant_key == item.category). The
// REVERSE of filterStoragesForItem. UX only — the server storageCategoryError guard enforces the pairing on
// post. Extracted here (613) as the ONE shared copy: DataEntryPage's 7 op forms + LinkedTransactionFields
// import it; no per-surface fork. No storage selected → returns the list unchanged (never over-narrows).
export function filterItemsForStorage(items, storages, storageId) {
  if (!storageId) return items;
  const stg = (storages || []).find(s => s.id === storageId);
  const ck = stg?.constant_key?.toLowerCase();
  if (!ck) return items;
  return items.filter(i => (i.category || '').toLowerCase() === ck);
}

// 466 — the Combobox-flavoured 3-level tree (LinkedTransactionFields uses a searchable Combobox, not a
// native <select>, so it can't take <optgroup>s). The Combobox renders one heading per option `group`,
// so: group = Constant display_label (level 1, live French); label = "<item-category> — <item>" carries
// levels 2 + 3. Same live labels + gated set as ItemOptgroups — grouping only, never a raw key.
export function itemComboOption(item) {
  const cat = String(item?.category || '').toLowerCase();
  const constantLabel = ITEM_CATEGORY_LABELS[cat] || 'Other';                 // level 1
  const catName = item?.item_category_labels?.name || constantLabel;          // level 2
  return { value: item.id, label: `${catName} — ${item.name}`, group: constantLabel };
}

// ONE grouping/ordering for item pickers and facets (Price List, Customer prices, Quotes, Reports): headings = the item category (live master-data label), canonical
// category order (the ITEM_CATEGORY_LABELS key order = category_constants sort_order), items alphabetical inside. Labels come from itemCategoryLabel's constant fallback, never a raw key.
export const constantRank = (cat) => { const i = Object.keys(ITEM_CATEGORY_LABELS).indexOf(String(cat || '').toLowerCase()); return i < 0 ? 999 : i; };
export function groupedItemOptions(items, { idKey = 'id', nameKey = 'name', labelWithCategory = false } = {}) {
  return [...(items || [])]
    .sort((a, b) => constantRank(a.category) - constantRank(b.category) || String(a[nameKey]).localeCompare(String(b[nameKey])))
    .map((it) => (labelWithCategory
      ? itemComboOption({ ...it, id: it[idKey], name: it[nameKey] })
      : { value: it[idKey], label: it[nameKey], group: ITEM_CATEGORY_LABELS[String(it.category || '').toLowerCase()] || 'Other' }));
}

function hydrate(rows) {
  if (!Array.isArray(rows) || !rows.length) return;   // keep fallbacks if empty
  // ITEM_CATEGORIES (in place, ordered by sort_order)
  ITEM_CATEGORIES.length = 0;
  rows.forEach((r) => ITEM_CATEGORIES.push(r.constant_key));
  // ITEM_CATEGORY_LABELS (in place) — display_label from the DB (reset to English, matching the FE).
  Object.keys(ITEM_CATEGORY_LABELS).forEach((k) => delete ITEM_CATEGORY_LABELS[k]);
  rows.forEach((r) => { ITEM_CATEGORY_LABELS[r.constant_key] = r.display_label; });
  // CAT_ATTACHED_TO_BAND (in place) — from the DB flag (T-4). Absent -> treat as band-attached.
  Object.keys(CAT_ATTACHED_TO_BAND).forEach((k) => delete CAT_ATTACHED_TO_BAND[k]);
  rows.forEach((r) => { CAT_ATTACHED_TO_BAND[r.constant_key] = r.attached_to_band !== false; });
  _loaded = true;
}

// Fetch once (deduped). Call after auth; safe to call repeatedly.
export function ensureCategoryConstants() {
  if (_loaded) return Promise.resolve(true);
  if (!_inflight) {
    _inflight = api.request('/category-constants')
      .then((r) => { hydrate((r && r.constants) || []); return _loaded; })
      .catch(() => { _inflight = null; return false; });   // allow retry after a failure
  }
  return _inflight;
}

// 463: force a fresh re-hydrate (the module is otherwise fetch-once). Call after a constant is CREATED
// or a display_label is EDITED so every reader (Settings>Categories picker/badges, the CC bottom table)
// reflects the LIVE constants + the owner's live labels without a manual page reload. Never writes a
// label — it only re-READS GET /api/category-constants and mutates the exported containers in place.
export function refetchCategoryConstants() {
  _loaded = false;
  _inflight = null;
  return ensureCategoryConstants();
}

export const isCategoryConstantsLoaded = () => _loaded;
export const getItemCategories = () => ITEM_CATEGORIES;
export const getCategoryLabels = () => ITEM_CATEGORY_LABELS;

// Hook: loads on mount, re-renders when ready. Use in surfaces that render category pickers/lists so
// they reflect the DB the moment it loads.
export function useCategoryConstants() {
  const [loaded, setLoaded] = useState(_loaded);
  useEffect(() => {
    if (_loaded) { setLoaded(true); return; }
    let alive = true;
    ensureCategoryConstants().then(() => { if (alive) setLoaded(_loaded); });
    return () => { alive = false; };
  }, []);
  return { isLoaded: loaded, itemCategories: ITEM_CATEGORIES, categoryLabels: ITEM_CATEGORY_LABELS };
}
